import React, { useMemo, useState } from "react";
import { HiOutlineMagnifyingGlass, HiOutlineUsers } from "react-icons/hi2";
import PlanBadge from "./PlanBadge";
import { formatCompactNumber, formatWithCommas } from "./format";

const PAGE_SIZE = 6;

const UsersTable = ({ users, onSelectUser }) => {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
    );
  }, [users, query]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageUsers = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleSearchChange = (e) => {
    setQuery(e.target.value);
    setPage(1);
  };

  return (
    <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-sm px-6 py-6">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-lg bg-violet-500 flex items-center justify-center text-white shadow-md flex-shrink-0">
            <HiOutlineUsers size={18} aria-hidden="true" />
          </span>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Users</h2>
            <p className="text-gray-500 font-medium text-sm">Click a row to view subscription history</p>
          </div>
        </div>
        <div className="relative">
          <HiOutlineMagnifyingGlass
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={16}
            aria-hidden="true"
          />
          <input
            type="text"
            value={query}
            onChange={handleSearchChange}
            placeholder="Search users..."
            aria-label="Search users"
            className="pl-9 pr-4 py-2 rounded-lg border-2 border-gray-200 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b-2 border-gray-200 text-left text-gray-600">
              <th className="px-4 py-3 font-bold">User</th>
              <th className="px-4 py-3 font-bold">Plan</th>
              <th className="px-4 py-3 font-bold text-right">Input tokens</th>
              <th className="px-4 py-3 font-bold text-right">Output tokens</th>
              <th className="px-4 py-3 font-bold text-right">Remaining tokens</th>
              <th className="px-4 py-3 font-bold text-right">Remaining credits</th>
            </tr>
          </thead>
          <tbody>
            {pageUsers.map((u) => (
              <tr
                key={u.id}
                onClick={() => onSelectUser(u)}
                className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                tabIndex={0}
                role="button"
                aria-label={`View subscription history for ${u.name}`}
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") && onSelectUser(u)
                }
              >
                <td className="px-4 py-4">
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full bg-indigo-100 border-2 border-indigo-200 text-indigo-600 font-bold flex items-center justify-center text-xs flex-shrink-0">
                      {u.initials}
                    </span>
                    <div>
                      <div className="font-bold text-gray-900">{u.name}</div>
                      <div className="text-gray-500 text-xs font-medium">{u.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <PlanBadge plan={u.plan} />
                  {/* <div className="text-gray-500 text-xs mt-1">{u.billing}</div> */}
                </td>
                <td className="px-4 py-4 text-right font-bold text-gray-900">
                  {formatCompactNumber(u.inputTokens)}
                </td>
                <td className="px-4 py-4 text-right font-bold text-gray-900">
                  {formatCompactNumber(u.outputTokens)}
                </td>
                <td className="px-4 py-4 text-right font-bold text-gray-900">
                  {formatCompactNumber(u.remainingTokens)}
                </td>
                <td className="px-4 py-4 text-right font-bold text-gray-900">
                  {formatWithCommas(u.remainingCredits)}
                  {/* <span className="text-gray-400 font-medium"> / {formatWithCommas(u.remainingCreditsCap)}</span> */}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4 text-sm text-gray-500 flex-wrap gap-3">
        <div>
          Showing <span className="font-semibold text-gray-900">{pageUsers.length}</span> of{" "}
          <span className="font-semibold text-gray-900">{filtered.length}</span> users
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={safePage <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Prev
          </button>
          <span className="text-gray-600">
            Page {safePage} / {pageCount}
          </span>
          <button
            type="button"
            disabled={safePage >= pageCount}
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default UsersTable;
